import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { Observable } from 'rxjs';
import {
  PIPELINE_BOARD_FILTER_CHIPS_CONFIG_TOKEN,
  PipelineBoardFilterChipsConfig,
} from './filter/pipeline-board-filter-chips/tokens';
import { PIPELINE_BOARD_SERVICE_CONFIG_TOKEN } from '../pipeline-board-service/tokens';
import {
  PIPELINE_SETTINGS_ACCESS_TOKEN,
  DEFAULT_CURRENCY_CODE_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
  PIPELINE_SETTINGS_URL_TOKEN,
} from '../tokens';
import { UserPipelinesModule } from '../user-pipelines/user-pipelines.module';
import { PipelineStatusCheckboxModule } from '../pipeline-status-checkbox/pipeline-status-checkbox.module';
import { LexiconModule } from '@galaxy/lexicon';
import baseTranslation from '../../assets/i18n/en_devel.json';
import { WEBLATE_COMPONENT_NAME } from '../../shared/constants';
import { PipelineStats } from '../pipeline-stats/pipeline-stats.service';
import { PACKAGE_SELECT_CONFIG_TOKEN } from '../../filters/package-selector/tokens';
import { SALES_TEAM_MULTI_SELECT_CONFIG_TOKEN } from '../../filters/salesteam-multi-select/tokens';
import { TAG_MULTI_SELECT_CONFIG_TOKEN } from '../../filters/tag-selector/tokens';
import { PRODUCT_SELECTOR_CONFIG_TOKEN } from '../../filters/product-selector/tokens';
import { ProductSelectorModule } from '../../filters/product-selector/product-selector.module';
import { PotentialRevenueModule } from '../pipeline/potential-revenue/potential-revenue.module';
import { REVENUE_DEFAULT_CURRENCY_CODE_TOKEN } from '../pipeline/potential-revenue/tokens';
import {
  FILTERS_CACHE_SERVICE_TOKEN,
  PipelineFiltersCacheService,
} from '../pipeline-filters-cache/pipeline-filters-cache.service';
import { PIPELINE_SERVICE_INJECTION_TOKEN } from '../pipeline/pipeline-service.interface';
import { PipelineBoardService } from '../pipeline-board-service/pipeline-board.service';
import { PipelineBusinessNameService } from '../pipeline-business-name/pipeline-business-name.service';
import { PipelineBoardServiceModule } from '../pipeline-board-service/pipeline-board-service.module';
import { PipelineBoardConfig } from './interfaces';
import {
  pipelineSettingsAccessFactory,
  defaultCurrencyCodeFactory,
  packageSelectConfigFactory,
  pipelineSettingsURLFactory,
  productSelectorConfigFactory,
  salesTeamMultiSelectConfigFactory,
  tagMultiSelectConfigFactory,
} from '../shared';
import { PipelineBoardServiceConfig } from '../pipeline-board-service/interfaces';
import { PipelineCardModule } from '../pipeline-card/pipeline-card.module';
import {
  SALESPERSON_MULTI_SELECT_CONFIG_TOKEN,
  SalespersonMultiSelectConfig,
} from '../../filters/salesperson-multi-select/tokens';
import { PipelineBoardFiltersModule } from './filter/pipeline-board-filters.module';
import { CURRENT_PIPELINE, Pipeline, USER_PIPELINES_TOKEN } from '@vendasta/sales-common';

export const PIPELINE_BOARD_CONFIG_TOKEN = new InjectionToken<PipelineBoardConfig>(
  'general dependency data for the pipeline board config',
);

@NgModule({
  imports: [
    UserPipelinesModule,
    PipelineStatusCheckboxModule,
    ProductSelectorModule,
    PotentialRevenueModule,
    PipelineBoardServiceModule,
    PipelineCardModule,
    PipelineBoardFiltersModule,
    LexiconModule.forChild({
      componentName: WEBLATE_COMPONENT_NAME,
      baseTranslation: baseTranslation,
    }),
  ],
  exports: [],
  providers: [PipelineStats],
})
export class PipelineBoardModule {
  static forRoot(config: InjectionToken<PipelineBoardConfig>): ModuleWithProviders<PipelineBoardModule> {
    return {
      ngModule: PipelineBoardModule,
      providers: [
        { provide: PIPELINE_BOARD_CONFIG_TOKEN, useExisting: config },
        {
          provide: FILTERS_CACHE_SERVICE_TOKEN,
          useExisting: PipelineFiltersCacheService,
        },
        { provide: PIPELINE_SERVICE_INJECTION_TOKEN, useExisting: PipelineBoardService },
        {
          provide: REVENUE_DEFAULT_CURRENCY_CODE_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: defaultCurrencyCodeFactory,
        },
        {
          provide: PACKAGE_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: packageSelectConfigFactory,
        },
        {
          provide: SALESPERSON_MULTI_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: salespersonMultiSelectConfigFactory,
        },
        {
          provide: SALES_TEAM_MULTI_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: salesTeamMultiSelectConfigFactory,
        },
        {
          provide: PRODUCT_SELECTOR_CONFIG_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: productSelectorConfigFactory,
        },
        {
          provide: TAG_MULTI_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: tagMultiSelectConfigFactory,
        },
        {
          provide: DEFAULT_CURRENCY_CODE_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: defaultCurrencyCodeFactory,
        },
        {
          provide: PIPELINE_SETTINGS_URL_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: pipelineSettingsURLFactory,
        },
        {
          provide: PIPELINE_SETTINGS_ACCESS_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: pipelineSettingsAccessFactory,
        },
        {
          provide: PIPELINE_BOARD_SERVICE_CONFIG_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: pipelineBoardServiceConfigFactory,
        },
        { provide: PARTNER_ID_TOKEN, deps: [PIPELINE_BOARD_CONFIG_TOKEN], useFactory: partnerIdFactory },
        { provide: MARKET_ID_TOKEN, deps: [PIPELINE_BOARD_CONFIG_TOKEN], useFactory: marketIdFactory },
        {
          provide: PIPELINE_BOARD_FILTER_CHIPS_CONFIG_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: pipelineBoardFilterChipsConfigFactory,
        },
        {
          provide: USER_PIPELINES_TOKEN,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: userPipelinesFactory,
        },
        {
          provide: CURRENT_PIPELINE,
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: currentPipelineFactory,
        },
        {
          provide: 'SALESPERSON_ID_TOKEN',
          deps: [PIPELINE_BOARD_CONFIG_TOKEN],
          useFactory: (config: PipelineBoardConfig) => config.salespersonId$,
        },
        PipelineBusinessNameService,
      ],
    };
  }
}

function pipelineBoardServiceConfigFactory(config: PipelineBoardConfig): PipelineBoardServiceConfig {
  return {
    marketId$: config.marketId$,
    partnerId$: config.partnerId$,
    salespersonId$: config.salespersonId$,
    pipelines$: config.pipelines$,
    currentPipeline$: config.currentPipeline$,
  };
}

function partnerIdFactory(config: PipelineBoardConfig): Observable<string> {
  return config.partnerId$;
}

function marketIdFactory(config: PipelineBoardConfig): Observable<string> {
  return config.marketId$;
}

function pipelineBoardFilterChipsConfigFactory(config: PipelineBoardConfig): PipelineBoardFilterChipsConfig {
  return {
    allSalespeople$: config.allSalespeople$,
    defaultCurrency$: config.defaultCurrency$,
    opportunityInfoUrlBuilder: config.opportunityInfoUrlBuilder,
    hasAccessToAllAccountsInMarket$: config.hasAccessToAllAccountsInMarket$,
    salesTeamsService: config.salesTeamsService,
    detailsConfig: config.detailsConfig,
  };
}

function userPipelinesFactory(config: PipelineBoardConfig): Observable<Pipeline[]> {
  return config.pipelines$;
}

export function salespersonMultiSelectConfigFactory(config: PipelineBoardConfig): SalespersonMultiSelectConfig {
  return {
    allSalespeople$: config.allSalespeople$,
    salespersonId$: config.salespersonId$,
    salespersonName$: config.salespersonName$,
    initWithLoggedInSalesperson: config.initWithLoggedInSalesperson,
  };
}

function currentPipelineFactory(config: PipelineBoardConfig): Observable<Pipeline> {
  return config.currentPipeline$;
}
